@let isEdit = edit$ | async;

<zef-sidenav [loading]="loading$ | async" [confirmClose]="!!(dataUpdates$ | async)">
  <ng-container *zefSidenavBanner>
    @if (showPartnerDropdown$ | async) {
      <div
        class="z-fx z-fx-none z-fx-start-center z-fx-gap-8 zef-card-8-16"
        [ngClass]="(helpCenterTeam$ | async) === zeffiTeam ? 'zef-bgcolor-ink' : 'zef-bgcolor-partner'"
      >
        <span class="zef-text-p2 z-fx-none zef-text-no-wrap">
          @if (isEdit) {
            <ng-container i18n>Editing help center article for:</ng-container>
          } @else {
            <ng-container i18n>Displaying help center article for:</ng-container>
          }
        </span>
        <zef-select
          [value]="helpCenterTeam$ | async"
          [class.zef-color-primary-inverted]="(helpCenterTeam$ | async) === zeffiTeam"
          class="invert-color z-fx-flex"
          type="inline-p2"
          (change)="selectTeam($event)"
        >
          @if (isZefAdmin$ | async) {
            <zef-select-option [value]="zeffiTeam"><brand-name></brand-name></zef-select-option>
          }
          @for (partner of partnerTeams$ | async; track trackByPartner($index, partner)) {
            <zef-select-option [value]="partner.$key">
              {{ partner.name }}
            </zef-select-option>
          }
        </zef-select>
      </div>
    }
  </ng-container>

  <ng-container *zefSidenavTitle>
    @if (isEdit) {
      @if (title$ | async; as title) {
        <zef-inline-edit
          class="z-fx-min-width"
          placeholder="Enter title..."
          i18n-placeholder
          [value]="title"
          (valueChange)="updateData({ title: $event })"
        ></zef-inline-edit>
      } @else {
        <mat-form-field class="zef-input-inline zef-input-big zef-input-no-padding">
          <input
            matInput
            ngModel
            placeholder="Enter title..."
            i18n-placeholder
            [ngModelOptions]="{ updateOn: 'blur' }"
            (ngModelChange)="updateData({ title: $event })"
          />
        </mat-form-field>
      }
    } @else {
      <ng-container>{{ title$ | async }}</ng-container>
    }
  </ng-container>

  <ng-template zefSidenavButtonSecondary>
    <div class="z-fx z-fx-end-center">
      @if ((isZefAdmin$ | async) && (isPartnerEdit$ | async) === false) {
        @if (stats$ | async; as stats) {
          <div class="z-fx-sc z-fx-gap-4 zef-text-p2-muted">
            <div matTooltip="Hovers" i18n-matTooltip>{{ stats.current.hovers }} ({{ stats.total.hovers }})</div>
            <span>•</span>
            <div matTooltip="Clicks" i18n-matTooltip>{{ stats.current.clicks }} ({{ stats.total.clicks }})</div>
            <span>•</span>
            <div matTooltip="External clicks" i18n-matTooltip>
              {{ stats.current.externalClicks }} ({{ stats.total.externalClicks }})
            </div>
          </div>
        }
        <mat-divider [vertical]="true" class="zef-divider-16"></mat-divider>
      }

      <div class="z-fx-sc z-fx-gap-4">
        @for (lang of languages; track lang) {
          <mat-button-toggle
            class="zef-icon-toggle"
            [value]="lang"
            [checked]="(lang$ | async) === lang"
            (change)="updateMode({ language: lang })"
          >
            <figure class="zef-flag-small" [ngClass]="'zef-lang-' + lang"></figure>
          </mat-button-toggle>
        }
      </div>

      <mat-divider [vertical]="true" class="zef-divider-16"></mat-divider>
    </div>
  </ng-template>

  <div *zefSidenavButtonPrimary class="z-fx-sc z-fx-gap-4">
    @if (read$ | async) {
      @if (externalLink$ | async) {
        <a
          [href]="externalLink$ | async"
          rel="nofollow"
          target="_blank"
          mat-raised-button
          color="ink"
          (click)="increment('externalClicks')"
        >
          <mat-icon>action_academy</mat-icon>
          <span i18n>Read more in Help Center</span>
        </a>
      }
    } @else {
      <button
        mat-raised-button
        color="success"
        [disabled]="quickTipError$ | async"
        [loading]="saving$ | async"
        (click)="onUpdate()"
        i18n
      >
        Update
      </button>
    }

    @if (canEdit$ | async) {
      <button mat-icon-button color="secondary" [matMenuTriggerFor]="moreMenu">
        <mat-icon>more</mat-icon>
        <mat-menu class="zef-menu-combo" #moreMenu>
          @if ((edit$ | async) === false) {
            <button mat-menu-item class="zef-menu-item-primary" (click)="updateMode({ mode: modes.Edit })">
              <mat-icon>edit</mat-icon>
              <span i18n>Edit</span>
            </button>
          }
          @if (edit$ | async) {
            @if ((preview$ | async) === false) {
              <button mat-menu-item (click)="updateMode({ mode: modes.Preview })">
                <mat-icon>visible_on</mat-icon>
                <span i18n>Preview</span>
              </button>
            }
            <button mat-menu-item (click)="updateMode({ mode: modes.Read })">
              <mat-icon>back</mat-icon>
              <span i18n>Cancel</span>
            </button>
          }
          @if (isPartnerEdit$ | async) {
            <button
              mat-menu-item
              class="zef-menu-item-alert"
              [disabled]="(data$ | async)?.hasParentData === false"
              [inlineDialogTriggerFor]="revertDataDialog"
            >
              <mat-icon>reset</mat-icon>
              <span i18n>
                Revert to
                <brand-name></brand-name>
              </span>
              <zef-inline-dialog [loading]="reverting$ | async" (confirm)="revert(data.subject)" #revertDataDialog>
                <ng-container *dialogTitle i18n>Revert Help Center Article</ng-container>
                <ng-container *dialogContent i18n>
                  Revert the custom Help Center Article data to
                  <brand-name></brand-name>
                  's Article
                </ng-container>
                <ng-container *dialogAction i18n>Revert Article</ng-container>
              </zef-inline-dialog>
            </button>
          }
        </mat-menu>
      </button>
    }
  </div>

  <div *zefSidenavContent class="z-fx-col zef-card-32 ql-snow">
    @if (data$ | async; as data) {
      @if (isEdit) {
        <div class="z-fx-col z-fx-gap-32">
          <div class="z-fx z-fx-gap-16">
            <div class="z-fx-col z-fx-flex z-fx-gap-16 help-quick-tip">
              <div class="z-fx-sc z-fx-gap-8 zef-text-p2">
                <div i18n>Quick tip</div>
                <div [ngClass]="(quickTipError$ | async) ? 'zef-color-alert' : 'zef-color-success'">
                  {{ quickTipTextArea.value.length }}/{{ maxChars }}
                </div>
                @if (data.quickTip) {
                  <div class="z-fx-sc zef-text-lightest help-test">
                    <div i18n>Test</div>
                    <button
                      mat-icon-button
                      [helpTooltip]="data.quickTip"
                      matTooltipPosition="above"
                      [matTooltipShowDelay]="300"
                    >
                      <mat-icon class="zef-icon-small" color="ink-lightest">help_center</mat-icon>
                    </button>
                  </div>
                }
              </div>
              <mat-form-field
                class="zef-textarea-small zef-textarea-space"
                [class.zef-textarea-error]="quickTipError$ | async"
              >
                <textarea
                  placeholder="This is a quick tip about the subject. There is a character limit set to 160 to maintain better readability."
                  i18n-placeholder
                  matInput
                  autoSize
                  [autoSizeMinRows]="3"
                  [ngModel]="data.quickTip"
                  (ngModelChange)="updateData({ quickTip: $event })"
                  #quickTipTextArea
                ></textarea>
              </mat-form-field>
            </div>
            <div class="z-fx-col z-fx-flex z-fx-gap-16">
              <div class="zef-text-p2" i18n>External Help Center link</div>
              <mat-form-field class="zef-input-small zef-input-space">
                <input
                  placeholder="https://zeffi.hs-sites.com/en/knowledge/knowledge-base-article"
                  i18n-placeholder
                  matInput
                  [ngModel]="data.externalLink"
                  (ngModelChange)="updateData({ externalLink: $event })"
                />
              </mat-form-field>
            </div>
          </div>
          <div class="z-fx-col z-fx-gap-16">
            <div class="zef-text-p2" i18n>Article</div>
            <quill-editor
              class="zef-quill space"
              [customOptions]="customOptions"
              [customModules]="customModules"
              [modules]="moduleConfig"
              [ngModel]="data.article || ''"
              placeholder="Article content..."
              i18n-placeholder
              (onEditorCreated)="onEditorCreated($event)"
              (ngModelChange)="updateData({ article: $event })"
            ></quill-editor>
          </div>
        </div>
      } @else {
        <help-article class="zef-text-p1 ql-editor" [article]="data.parsedArticle | safeHtml"></help-article>
      }
    }
  </div>
</zef-sidenav>
